import { todayIsoDate } from '@/modules/reservations/utils/dateRange';
import type { CalendarViewMode, VisibleDateRange } from '@/modules/calendar/types';

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  return toIsoDate(d);
}

export function addMonths(iso: string, months: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setMonth(d.getMonth() + months);
  return toIsoDate(d);
}

export function startOfMonth(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return toIsoDate(new Date(d.getFullYear(), d.getMonth(), 1));
}

export function endOfMonth(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return toIsoDate(new Date(d.getFullYear(), d.getMonth() + 1, 0));
}

/** Lunes como inicio de semana (ISO). */
export function startOfWeek(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return toIsoDate(d);
}

export function endOfWeek(iso: string): string {
  return addDays(startOfWeek(iso), 6);
}

export function getVisibleRange(
  view: CalendarViewMode,
  anchorDate: string,
): VisibleDateRange {
  if (view === 'day') {
    return { rangeStart: anchorDate, rangeEnd: anchorDate };
  }
  if (view === 'week') {
    return {
      rangeStart: startOfWeek(anchorDate),
      rangeEnd: endOfWeek(anchorDate),
    };
  }
  const monthStart = startOfMonth(anchorDate);
  const monthEnd = endOfMonth(anchorDate);
  return {
    rangeStart: startOfWeek(monthStart),
    rangeEnd: endOfWeek(monthEnd),
  };
}

export type CalendarDayCell = {
  iso: string;
  inCurrentMonth: boolean;
};

/** Celdas de grilla mensual (6 semanas × 7 días). */
export function getMonthGridDays(anchorDate: string): CalendarDayCell[] {
  const first = startOfMonth(anchorDate);
  const gridStart = startOfWeek(first);
  const cells: CalendarDayCell[] = [];
  const month = new Date(`${anchorDate}T12:00:00`).getMonth();

  for (let i = 0; i < 42; i++) {
    const iso = addDays(gridStart, i);
    const inCurrentMonth =
      new Date(`${iso}T12:00:00`).getMonth() === month;
    cells.push({ iso, inCurrentMonth });
  }
  return cells;
}

export function getWeekDayIsos(anchorDate: string): string[] {
  const start = startOfWeek(anchorDate);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

export function getDefaultAnchorDate(): string {
  return todayIsoDate();
}
